import { useMutation, useQuery } from '@apollo/client';
import { LoadingOrError } from '@retainerclub/common';
import {
  ACTION_SUBSCRIPTION_CANCEL,
  ACTION_SUBSCRIPTION_DOWNGRADE_UPGRADE,
  ACTION_SUBSCRIPTION_PAUSE,
  ACTION_SUBSCRIPTION_UNPAUSE,
} from '@retainerclub/customer-api';
import {
  GET_SUBSCRIPTION_PRODUCTS,
  GET_SUBSCRIPTIONS,
} from '@retainerclub/shared-api';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { DISCLOSURE_VERSION } from '../../../SubscriptionDisclosure/helpers/disclosureCopy';
import ManageSubscriptionConfirmation from './ManageSubscriptionConfirmation/ManageSubscriptionConfirmation';
import ManageSubscriptionMainView from './ManageSubscriptionMainView/ManageSubscriptionMainView';
import ManageSubscriptionRetention from './ManageSubscriptionRetention/ManageSubscriptionRetention';
import ManageSubscriptionUnpause from './ManageSubscriptionUnpause/ManageSubscriptionUnpause';

// Apollo runs onError instead of onCompleted for network and GraphQL errors, so the modal stays
// open on whichever view the customer submitted from. Without an error to render it sits there
// saying nothing — the dead end that turned one downgrade into three. `path` is deliberately not a
// card-warning value so SubscriptionActionAlert renders its generic failure copy.
const UNEXPECTED_ACTION_ERROR = {
  message: "We couldn't complete that change.",
  path: 'unexpected_error',
};

function ManageSubscription({ subscription, handleCloseModal, refetch }) {
  const [currentView, setCurrentView] = useState(
    subscription?.state === 'paused' ? 'paused' : 'main',
  );
  const [selectedAction, setSelectedAction] = useState(null);
  const [subscriptionProducts, setSubscriptionProducts] = useState([]);
  const [now, setNow] = useState(false);
  const [actionError, setActionError] = useState(null);

  const { loading, error } = useQuery(GET_SUBSCRIPTION_PRODUCTS, {
    onCompleted: (data) => {
      if (data.subscriptionProducts?.length > 0) {
        const sortedSubs = [...data.subscriptionProducts].sort(
          (a, b) => a.price - b.price,
        );

        setSubscriptionProducts(sortedSubs);
      }
    },
  });

  // The `refetch` prop only refreshes the account query. The plan list on the dashboard is a
  // separate GET_SUBSCRIPTIONS query, so without this the customer's old plan stayed on screen
  // after a successful change and they submitted again — which is how one downgrade became three.
  // awaitRefetchQueries holds the spinner until the fresh list is in the cache, so the modal never
  // closes back onto stale rows.
  const subscriptionMutationOptions = {
    refetchQueries: [{ query: GET_SUBSCRIPTIONS }],
    awaitRefetchQueries: true,
  };

  const [updateSubscription, { loading: updateLoading }] = useMutation(
    ACTION_SUBSCRIPTION_DOWNGRADE_UPGRADE,
    {
      ...subscriptionMutationOptions,
      onCompleted: (data) => {
        // Any errorMessages entry means the backend left the subscription untouched and charged
        // nothing. Keep the modal open so the customer can read it and act on it. The `path` tells
        // us whether it is a card problem or an unexpected failure, which read very differently.
        const [mutationError] =
          data?.actionSubscriptionDowngradeUpgrade?.errorMessages ?? [];

        if (mutationError) {
          setActionError(mutationError);
          return;
        }

        refetch();
        handleCloseModal();
      },
      onError: (error) => {
        console.error('Subscription downgrade/upgrade failed:', error);
        setActionError(UNEXPECTED_ACTION_ERROR);
      },
    },
  );

  // Pause, unpause and cancel report refusals the same way the tier change does — an entry in
  // errorMessages with the subscription left untouched. Closing the modal on one of those would
  // tell the customer the action worked when it did not, which is what started this whole bug.
  function handleActionCompleted(errorMessages) {
    const [mutationError] = errorMessages ?? [];

    if (mutationError) {
      setActionError(mutationError);
      return;
    }

    refetch();
    handleCloseModal();
  }

  const [pauseSubscription, { loading: pauseLoading }] = useMutation(
    ACTION_SUBSCRIPTION_PAUSE,
    {
      ...subscriptionMutationOptions,
      onCompleted: (data) => {
        handleActionCompleted(data?.actionSubscriptionPause?.errorMessages);
      },
      onError: (error) => {
        console.error('Subscription pause failed:', error);
        setActionError(UNEXPECTED_ACTION_ERROR);
      },
    },
  );

  const [unpauseSubscription, { loading: unpauseLoading }] = useMutation(
    ACTION_SUBSCRIPTION_UNPAUSE,
    {
      ...subscriptionMutationOptions,
      onCompleted: (data) => {
        handleActionCompleted(data?.actionSubscriptionUnpause?.errorMessages);
      },
      onError: (error) => {
        console.error('Subscription unpause failed:', error);
        setActionError(UNEXPECTED_ACTION_ERROR);
      },
    },
  );

  const [cancelSubscription, { loading: cancelLoading }] = useMutation(
    ACTION_SUBSCRIPTION_CANCEL,
    {
      ...subscriptionMutationOptions,
      onCompleted: (data) => {
        handleActionCompleted(data?.actionSubscriptionCancel?.errorMessages);
      },
      onError: (error) => {
        console.error('Subscription cancellation failed:', error);
        setActionError(UNEXPECTED_ACTION_ERROR);
      },
    },
  );

  // Driven by Apollo rather than a manual flag. The old flag was set before the request and
  // cleared in the callbacks, so any path that missed a callback left the modal spinning.
  const isLoading =
    updateLoading || pauseLoading || unpauseLoading || cancelLoading;

  const retentionOffers = useMemo(() => {
    return [{ id: 1, discount: 15, description: '15% off your next renewal' }];
  }, []);

  function handleUpgrade(tier) {
    setActionError(null);
    setSelectedAction({ type: 'upgrade', tier, now });
    setCurrentView('confirmation');
  }

  function handleDowngrade(tier) {
    setActionError(null);
    setSelectedAction({ type: 'downgrade', tier, now });
    setCurrentView('confirmation');
  }

  function handleCancel() {
    setSelectedAction({ type: 'cancel' });

    setCurrentView('retention');
  }

  function handlePauseAction(days) {
    if (isLoading) {
      return;
    }

    setActionError(null);

    pauseSubscription({
      variables: {
        input: {
          input: {
            id: subscription.id,
            days,
          },
          clientMutationId: crypto.randomUUID(),
        },
      },
    });
  }

  function handleUnpauseAction() {
    if (isLoading) {
      return;
    }

    setActionError(null);

    unpauseSubscription({
      variables: {
        input: {
          id: subscription.id,
          clientMutationId: crypto.randomUUID(),
        },
      },
    });
  }

  function handleConfirmAction(acknowledgment) {
    // A submission is already in flight. Without this the Confirm button can fire again from a
    // queued tap before React has re-rendered into the loading view.
    if (isLoading) {
      return;
    }

    setActionError(null);

    updateSubscription({
      variables: {
        input: {
          input: {
            id: subscription.id,
            action: selectedAction.type,
            productId: selectedAction.tier?.product?.id,
            now,
            // The customer's agreement to the new terms, written to the consent ledger
            // with the change so the new price is provable later.
            acknowledgment: {
              agreed: acknowledgment?.agreed === true,
              disclosureVersion: DISCLOSURE_VERSION,
            },
          },
          clientMutationId: crypto.randomUUID(),
        },
      },
    });
  }

  function handleRetentionOffer(offer) {
    // TODO: Build out the retention offer logic
    if (isLoading) {
      return;
    }

    updateSubscription({
      variables: {
        input: {
          id: subscription.id,
          action: 'retention',
          offerId: offer.id,
        },
      },
    });
  }

  function handleFinalCancel() {
    if (isLoading) {
      return;
    }

    setActionError(null);

    cancelSubscription({
      variables: {
        input: {
          id: subscription.id,
          clientMutationId: crypto.randomUUID(),
        },
      },
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-rcprimary-400 border-b-2" />
          <p className="mt-2 text-gray-600 text-sm">
            Processing your request...
          </p>
        </div>
      </div>
    );
  }

  const shouldRender = LoadingOrError({
    loading,
    error,
    title: 'Manage Subscription',
  });

  if (shouldRender) {
    return (
      <div className="p-6">
        {currentView === 'main' && (
          <ManageSubscriptionMainView
            subscription={subscription}
            handleUpgrade={handleUpgrade}
            handleDowngrade={handleDowngrade}
            handleCancel={handleCancel}
            subscriptionProducts={subscriptionProducts}
            now={now}
          />
        )}
        {currentView === 'paused' && (
          <ManageSubscriptionUnpause
            subscription={subscription}
            handleUnpauseAction={handleUnpauseAction}
            handleCloseModal={handleCloseModal}
            actionError={actionError}
          />
        )}
        {currentView === 'confirmation' && (
          <ManageSubscriptionConfirmation
            selectedAction={selectedAction}
            handleConfirmAction={handleConfirmAction}
            setCurrentView={setCurrentView}
            isLoading={isLoading}
            now={now}
            setNow={setNow}
            subscription={subscription}
            actionError={actionError}
          />
        )}
        {currentView === 'retention' && (
          <ManageSubscriptionRetention
            retentionOffers={retentionOffers}
            handleRetentionOffer={handleRetentionOffer}
            setCurrentView={setCurrentView}
            handleFinalCancel={handleFinalCancel}
            isLoading={isLoading}
            handlePauseAction={handlePauseAction}
            actionError={actionError}
          />
        )}
      </div>
    );
  }

  return shouldRender;
}

ManageSubscription.propTypes = {
  subscription: PropTypes.object.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  refetch: PropTypes.func,
};

export default ManageSubscription;
